// Custom imports
import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DynamicFormItem } from '@ispm/shared/dynamic-form/classes/dynamic-form-item.class';
import { IdynamicFormItem } from '@ispm/shared/dynamic-form/interfaces/IdynamicFormItem';
import { Widget } from '../../decorator/widget.decorator';
import { IWidgetItem } from '../../interfaces/widget-item.interface';
import { QuestionnairesService } from './services/questionnaire.service';
import { isEmpty, isEqual } from 'lodash';
import { IQuestionareData, IQuestionnaireResponse } from './interfaces/questionnaire-response.interface';
import { Store } from '@ngrx/store';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { TestBarActionTypes, TestBarEnableConfirmButtonAction } from '@ispm/dashboard/test/store/test-bar.actions';
import { WorkflowConfigState } from '@ispm/workflow/store/workflow-config.state';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';

@Component({
  selector: 'ispm-questionnaires',
  templateUrl: './questionnaires.component.html',
  styleUrls: ['./questionnaires.component.scss']
})
@Widget('Questionnaire')
export class QuestionnairesComponent implements OnInit, IWidgetItem {
  widgetUpdateValueCallback: (value) => void;
  public loading: boolean = false;
  public init = false;
  private _widgetConfig: IdynamicFormItem = <IdynamicFormItem>{};
  public questionnaireData: Array<any>[] | {} = {};
  private lastSavedQuestionnaireData: Array<any>[] | {} = {};
  public inputConfig: DynamicFormItem[];
  public widgetFormInstance: FormGroup;
  public isValid: boolean = false;
  private _widgetFormInstanceSubmitted: boolean = false;
  public activeWorkflowConfirmationButton: string = '';
  private destroyed$: Subject<any> = new Subject();

  constructor(
    private questionnairesService: QuestionnairesService,
    private cd: ChangeDetectorRef,
    private store: Store<IgenomicsState>,
    private notificationMessageService: NotificationMessageService
  ) {
    this.getWorkflowConfirmationButton();
  }

  public ngOnInit(): void {
    this.loading = true;
    this.initializeQuestionnaires();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.cd.detach();
  }

  /**
   * Initialize questionnaires
   */
  private initializeQuestionnaires() {
    this.questionnairesService.getQuestionnairesInfo().pipe(takeUntil(this.destroyed$.asObservable())).subscribe((response) => {
      this.fetchQuestionnaireResponse(response[1]);
      this.getSavedQuestionnaire(response[0]);
      this.init = true;
      this.loading = false;
      this.cd.detectChanges();
    });
  }

  /**
   * Gets saved questionnaire data
   */
  public getSavedQuestionnaire(data) {
    if (data && data.questionnaire) {
      this.questionnaireData = { ...data.questionnaire };
      this.lastSavedQuestionnaireData = { ...data.questionnaire };
    }
    else {
      this.questionnaireData = {};
      this.lastSavedQuestionnaireData = {};
    }
  }

  /**
   * Fetches questionnaire response from API
   */
  private fetchQuestionnaireResponse(questionnaire: IQuestionnaireResponse[]) {
    let questionnaireResponse: IQuestionnaireResponse[] = [...questionnaire];
    if (questionnaireResponse && !isEmpty(questionnaire)) {
      this.inputConfig = questionnaireResponse.map((response: IQuestionnaireResponse) => this.buildFormItems(response));
    }
  }

  /**
   * Getter for widget config
   */
  get widgetConfig(): any {
    return this._widgetConfig;
  }

  /**
   * Setter for widget config
   * @param config holds widget config data
   */
  set widgetConfig(config: any) {
    this._widgetConfig = { ...config, name: 'CHECKLIST' };
  }

  /**
   * Build individual form items
   * @param input holds form input
   * @returns DynamicFormItem
   */
  private buildFormItems(input: IQuestionnaireResponse): DynamicFormItem {
    const invalidFormTooltip = { "[invalidTooltipText]": "Response is invalid!" };
    input.attributes = {...input.attributes, ...invalidFormTooltip};
    return DynamicFormItem.builder(
      input.label || '',
      input.name,
      input.componentType
    ).setCssClass(input.cssClass).setPlaceholder(input.placeholder).setOptions(input.valueSet)
      .setAttributes(input.attributes).setRequired(input.required).setEditable(true)
      .setRegex(input.regex).setDefault(null).
      addReflects(...this.questionnairesService.setDependentQuestionnairesReflects(input.hasDependent));
  }

  /**
   * Calls on validating the form
   * @param validate holds boolean value
   */
  public updateValidate(validate: boolean) {
    if (this.activeWorkflowConfirmationButton === 'documentsPreview') {
      if (validate && isEqual(this.lastSavedQuestionnaireData, this.questionnaireData)) {
        this.dispatchToStore(true, '');
      } else {
        this.dispatchToStore(false, 'Please update the Checklist!');
      }
    }
    this.isValid = validate;
  }

  /**
   * Dispatches data to store
   * @param enableButton holds boolean value to enable button
   * @param tooltipText holds tooltipText
   */
  private dispatchToStore(enableButton: boolean, tooltipText: string) {
    this.store.dispatch(new TestBarEnableConfirmButtonAction(enableButton));
    this.store.dispatch({
      type: TestBarActionTypes.EnableConfirmButtonTooltip,
      payload: tooltipText
    });
  }

  /**
   * Return the getWorkflow Confirmation Button name
   */
  private getWorkflowConfirmationButton() {
    this.store.select('workflowConfig').take(1).subscribe((data: WorkflowConfigState) => {
      if (data && data.activeWorkflow.activeJobName) {
        this.activeWorkflowConfirmationButton = data.activeWorkflow.activeJobName;
      }
    });
  }

  /**
   * Setter for widget data
   */
  set widgetData(_widgetData: any) { }

  /**
   * Getter for widget submit status
   */
  public get widgetFormInstanceSubmitted() {
    return this._widgetFormInstanceSubmitted;
  }

  /**
   * Setter for widget submit status
   */
  public set widgetFormInstanceSubmitted(widgetFormInstanceSubmitted: boolean) {
    this._widgetFormInstanceSubmitted = widgetFormInstanceSubmitted;
  }

  /**
   * Calls on each form item change
   * @param event holds updated form data
   */
  public updateFormOnChange(event) {
    this.questionnaireData = event;
    this.unSavedDataCheck();
  }
 
  /**
   * To check unsaved data
   */

  private unSavedDataCheck() {
    /***
     * First condition when checklist load first time lastSavedQuestionnaireData will be empty
     *  object and questionnaireData will be having some null values with keys.
     * Second condition when lastSavedQuestionnaireData is empty object but questionnaireData is updated.
     * Third condition when both lastSavedQuestionnaireData and questionnaireData are not equal and should have value.
     * 
     */
    if( Object.keys(this.lastSavedQuestionnaireData).length === 0 ){
      if(this.isQuestionUpdatedOnClick(this.questionnaireData)) {
        this.questionnairesService.isChecklistFormUpdated$.next(true);
      }
    } 
    if (!isEqual(this.lastSavedQuestionnaireData, this.questionnaireData) && Object.keys(this.lastSavedQuestionnaireData).length) {
      this.questionnairesService.isChecklistFormUpdated$.next(true);
    }
  }
  /**
   * To check if question is updated on click
   */

  private isQuestionUpdatedOnClick(questionData:any) {
    for (let key in questionData) {
        if (questionData[key] !== null && questionData[key] != "" && questionData[key] !== undefined)
            return true;
    }
    return false;
}


  /**
   * Calls on Update button click
   */
  public saveQuestionnaireData() {
    this.widgetFormInstanceSubmitted = true;
    this.loading = true;
    let payload: IQuestionareData = <IQuestionareData>{};
    payload.questionnaire = { ...this.questionnaireData };
    this.questionnairesService.postQuestionnaireData(payload, this.isValid).pipe(takeUntil(this.destroyed$.asObservable())).subscribe(() => {
      this.postSaveActions();
    },
      error => {
        this.loading = false;
        this.cd.detectChanges();
        this.notificationMessageService.error('Failed to update checklist!');
      });
  }

  /**
   * Calls after questionnaire is updated
   */
  private postSaveActions() {
    this.loading = false;
    this.lastSavedQuestionnaireData = { ...this.questionnaireData };
    this.cd.detectChanges();
    this.notificationMessageService.success('Checklist updated successfully!');
    this.updateValidate(this.isValid);
    this.questionnairesService.isChecklistFormUpdated$.next(false);
  }
}
